import { isKeyDefined } from '../UNSAFE_keys';

/**
 * Returns the key from the item element
 */
const getKey = (item: HTMLElement) => {
  return item.dataset['ojKeyType'] === 'number'
    ? Number(item.dataset['ojKey'])
    : item.dataset['ojKey'];
};

/**
 * A helper function that return the key of the first available item.
 */
export const getFirstVisibleKey = (root: HTMLElement, itemSelector: string) => {
  if (root) {
    const firstItem = root.querySelector(itemSelector);
    if (firstItem) {
      const key = getKey(firstItem as HTMLElement);
      return key;
    }
  }
  return null;
};

/**
 * A helper function to find the element with the specified key.
 * Returns null if the element with key is not found.
 */
export const findElementByKey = <K extends string | number>(
  root: HTMLElement,
  key: K,
  itemSelector: string
) => {
  if (root) {
    const items = root.querySelectorAll(itemSelector);
    for (let i = 0; i < items.length; i++) {
      const thisKey = getKey(items[i] as HTMLElement);
      if (key == (thisKey as K)) {
        return items[i];
      }
    }
  }
  return null;
};

/**
 * A helper function that returns the key of the previous or the next item
 * given the specified current key
 */
export const getPrevNextKey = <K extends string | number>(
  root: HTMLElement | null,
  currentKey: K | undefined,
  isPrev: boolean,
  itemSelector: string
) => {
  return (): K | null => {
    if (isKeyDefined(currentKey) && root) {
      const currItem = findElementByKey(root, currentKey as K, itemSelector);
      if (currItem) {
        const nextItem = isPrev ? currItem.previousElementSibling : currItem.nextElementSibling;
        if (nextItem) {
          const key = getKey(nextItem as HTMLElement) as K;
          if (key != null) {
            return key;
          }
        }
      }
      return getFirstVisibleKey(root, itemSelector) as K;
    }
    return null;
  };
};

/**
 * A helper function to get the key from an element
 */
export const keyExtractor = <K extends string | number>(
  element: HTMLElement,
  itemSelector: string
): K | null => {
  const item = element.closest(itemSelector);
  if (item) {
    const key = getKey(item as HTMLElement) as K;
    return key === undefined ? null : key;
  }
  return null;
};
